import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as THREE from 'three';
import { Subscription } from 'rxjs';
import { NzModalService } from 'ng-zorro-antd/modal';
import {
  FikoCadDiffService,
  CadDiffSonuc,
  getCadDiffService,
  ComparisonMode,
  ComparisonType,
  ViewMode,
  RGB,
  CadDiffColors,
  DiffModelSecim
} from './FikoCadDiffService';

// Re-export for backwards compatibility
export type { DiffModelSecim };

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * FikoCAD Diff Component - CATIA V5 Compare Products Tarzı
 * ═══════════════════════════════════════════════════════════════════════════════
 */

@Component({
  selector: 'fikoyt-fiko-cad-diff',
  templateUrl: './fiko-cad-diff.component.html',
  styleUrls: ['./fiko-cad-diff.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FikoCadDiffComponent implements OnDestroy, OnInit {

  // ═══════════════════════════════════════════════════════════════════════════
  // INPUT / OUTPUT
  // ═══════════════════════════════════════════════════════════════════════════

  private _pencere: any;
  @Input() set pencere(v: any) { this._pencere = v?.instance || v; }
  get pencere(): any { return this._pencere; }

  private _sahne: any;
  @Input() set sahne(v: any) {
    this._sahne = v;
    if (v) {
      this.modelleriTara();
      this.kurSecimDinleyici();
    }
  }
  get sahne(): any { return this._sahne; }

  // ═══════════════════════════════════════════════════════════════════════════
  // SERVİS & STATE
  // ═══════════════════════════════════════════════════════════════════════════

  private diffService: FikoCadDiffService;
  private treeSubscription: Subscription | null = null;
  private canvasClickHandler: ((event: MouseEvent) => void) | null = null;
  private documentClickHandler: ((event: MouseEvent) => void) | null = null;

  mevcutModeller: DiffModelSecim[] = [];
  modelA: DiffModelSecim | null = null;
  modelB: DiffModelSecim | null = null;
  secimModu: 'yok' | 'A' | 'B' = 'yok';
  dropdownAcikA = false;
  dropdownAcikB = false;
  sonuc: CadDiffSonuc | null = null;

  private vizGrup: THREE.Group | null = null;
  private heatmapMeshA: THREE.Mesh | null = null;
  private heatmapMeshB: THREE.Mesh | null = null;

  // Ghost mode: original model material backup
  private savedMaterialsA: Map<THREE.Mesh, THREE.Material | THREE.Material[]> = new Map();
  private savedMaterialsB: Map<THREE.Mesh, THREE.Material | THREE.Material[]> = new Map();
  private ghostMaterial: THREE.MeshStandardMaterial | null = null;

  // CATIA Options
  comparisonMode: ComparisonMode = 'geometric';
  comparisonType: ComparisonType = 'added_removed';
  viewMode: ViewMode = 'both';
  computationAccuracy = 1.0;  // mm
  displayAccuracy = 1.0;      // mm
  visualAccuracy = 0.4;       // mm
  useLocalAxis = false;

  // Renk ayarları - CATIA V5 Professional Palette
  colors: CadDiffColors = {
    same: { r: 0.78, g: 0.72, b: 0.32 },    // Warm gold (CATIA style)
    removed: { r: 0.82, g: 0.18, b: 0.32 },  // Coral-magenta (professional red)
    added: { r: 0.18, g: 0.52, b: 0.82 }     // Cool steel blue (professional blue)
  };
  showEdges = true;
  edgeColor: RGB = { r: 0.15, g: 0.15, b: 0.15 };
  useHeatmapGradient = false;  // Gradient heatmap (varsayılan kapalı → eski binary renklendirme)
  refineBoundary = true;       // Sınır üçgenlerini subdivide et (varsayılan açık)
  showOnlyDifferences = false; // Sadece farklı bölgeleri göster
  overlayMode = false;         // Overlay: iki modeli üst üste bindirme

  // Gelişmiş ayarlar
  surfaceFilterMultiplier = 10;  // Tessellation artifact filtre çarpanı (tolerance * N)
  subdivisionDepth = 3;          // Sınır üçgen subdivision derinliği
  ghostOpacity = 0.20;           // Ghost mode opaklığı (0-1) - daha görünür
  activePreset: 'hassas' | 'standart' | 'hizli' = 'standart';

  // Edge mesh'leri (Group veya LineSegments olabilir)
  private edgeLinesA: THREE.Object3D | null = null;
  private edgeLinesB: THREE.Object3D | null = null;

  // Legacy (for compatibility)
  tolerans = 0.01;

  islemYapiliyor = false;
  orijinallerGizli = true;

  // Progress
  progressYuzde = 0;
  progressMesaj = '';

  private modalService = inject(NzModalService);

  constructor(public CD: ChangeDetectorRef) {
    this.diffService = getCadDiffService();
  }

  ngOnInit(): void {
    this.documentClickHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.model-kutu')) {
        this.dropdownAcikA = false;
        this.dropdownAcikB = false;
        this.CD.detectChanges();
      }
    };
    document.addEventListener('click', this.documentClickHandler);
  }

  ngOnDestroy(): void {
    this.temizle();
    this.temizleSecimDinleyici();
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SEÇİM DİNLEYİCİ
  // ═══════════════════════════════════════════════════════════════════════════

  private kurSecimDinleyici(): void {
    this.temizleSecimDinleyici();

    const anaGosterim = this._sahne?.anaGosterim;
    if (!anaGosterim) return;

    const canvas = anaGosterim.sunucu?.domElement;
    const kamera = anaGosterim.aktifKamera;
    if (!canvas || !kamera) return;

    this.canvasClickHandler = (event: MouseEvent) => {
      if (this.secimModu === 'yok') return;

      const rect = canvas.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      );

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, kamera);

      const sahneRoot = this._sahne.urunAgaci || this._sahne;
      const intersects = raycaster.intersectObjects(sahneRoot.children, true);

      if (intersects.length > 0) {
        const uygunNesne = this.karsilastirilabirNesneBul(intersects[0].object);
        if (uygunNesne) {
          this.modelSec(uygunNesne, 'sahne');
        }
      }
    };
    canvas.addEventListener('click', this.canvasClickHandler);

    const secimler = this._sahne?.secimler;
    if (secimler?.treeStateChanged) {
      this.treeSubscription = secimler.treeStateChanged.subscribe((state: any) => {
        if (this.secimModu === 'yok') return;

        const selectedUuids = state?.selectedUuids;
        if (!selectedUuids || selectedUuids.size === 0) return;

        const firstUuid = selectedUuids.values().next().value;
        if (!firstUuid) return;

        const sahneRoot = this._sahne.urunAgaci || this._sahne;
        let bulunanNesne: THREE.Object3D | null = null;

        sahneRoot.traverse((child: THREE.Object3D) => {
          if (child.uuid === firstUuid) bulunanNesne = child;
        });

        if (bulunanNesne) {
          const uygunNesne = this.karsilastirilabirNesneBul(bulunanNesne);
          if (uygunNesne) {
            this.modelSec(uygunNesne, 'agac');
          }
        }
      });
    }
  }

  private temizleSecimDinleyici(): void {
    if (this.treeSubscription) {
      this.treeSubscription.unsubscribe();
      this.treeSubscription = null;
    }
    if (this.canvasClickHandler && this._sahne?.anaGosterim?.sunucu?.domElement) {
      this._sahne.anaGosterim.sunucu.domElement.removeEventListener('click', this.canvasClickHandler);
      this.canvasClickHandler = null;
    }
  }

  private modelSec(nesne: THREE.Object3D, kaynak: 'sahne' | 'agac'): void {
    const model: DiffModelSecim = {
      uuid: nesne.uuid,
      isim: nesne.name || `Nesne_${nesne.id}`,
      nesne: nesne,
      kaynak
    };

    if (this.secimModu === 'A') {
      this.modelA = model;
    } else if (this.secimModu === 'B') {
      this.modelB = model;
    }

    this.secimModu = 'yok';
    this.sonuc = null;
    this.CD.detectChanges();
  }

  private karsilastirilabirNesneBul(nesne: THREE.Object3D): THREE.Object3D | null {
    let hasMesh = false;
    nesne.traverse((child) => {
      if (child instanceof THREE.Mesh && child.geometry) hasMesh = true;
    });
    if (hasMesh) return nesne;

    let current: THREE.Object3D | null = nesne;
    while (current) {
      if ((current as any).type === 'FikoCizimYalin') return current;
      current = current.parent;
    }

    return null;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MODEL LİSTESİ
  // ═══════════════════════════════════════════════════════════════════════════

  modelleriTara(): void {
    this.mevcutModeller = [];
    const eklenenIsimler = new Set<string>();

    if (!this._sahne) return;

    const sahneRoot = this._sahne.urunAgaci || this._sahne;

    sahneRoot.traverse((child: THREE.Object3D) => {
      if ((child as any).type === 'FikoCizimYalin') {
        const isim = child.name || `STEP_${child.id}`;
        if (eklenenIsimler.has(isim)) return;

        this.mevcutModeller.push({
          uuid: child.uuid,
          isim,
          nesne: child,
          kaynak: 'agac'
        });
        eklenenIsimler.add(isim);
      }
    });

    this.CD.detectChanges();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UI ACTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  dropdownAToggle(): void {
    this.dropdownAcikA = !this.dropdownAcikA;
    this.dropdownAcikB = false;
    this.CD.detectChanges();
  }

  dropdownBToggle(): void {
    this.dropdownAcikB = !this.dropdownAcikB;
    this.dropdownAcikA = false;
    this.CD.detectChanges();
  }

  modelASecildi(model: DiffModelSecim): void {
    this.modelA = model;
    this.secimModu = 'yok';
    this.dropdownAcikA = false;
    this.sonuc = null;
    this.CD.detectChanges();
  }

  modelBSecildi(model: DiffModelSecim): void {
    this.modelB = model;
    this.secimModu = 'yok';
    this.dropdownAcikB = false;
    this.sonuc = null;
    this.CD.detectChanges();
  }

  modelleriDegistir(): void {
    const temp = this.modelA;
    this.modelA = this.modelB;
    this.modelB = temp;
    this.sonuc = null;
    this.CD.detectChanges();
  }

  sahnedenSec(hedef: 'A' | 'B'): void {
    this.secimModu = hedef;
    this.CD.detectChanges();
  }

  secimIptal(): void {
    this.secimModu = 'yok';
    this.CD.detectChanges();
  }

  iptalEt(): void {
    this.diffService.abort();
    this.islemYapiliyor = false;
    this.progressYuzde = 0;
    this.progressMesaj = 'Cancelled';
    this.CD.detectChanges();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // KARŞILAŞTIRMA
  // ═══════════════════════════════════════════════════════════════════════════

  async karsilastir(): Promise<void> {
    if (!this.modelA || !this.modelB) return;

    this.islemYapiliyor = true;
    this.progressYuzde = 0;
    this.progressMesaj = 'Starting...';
    this.CD.detectChanges();

    // Set service options
    const accuracy = this.comparisonMode === 'geometric'
      ? this.computationAccuracy
      : this.visualAccuracy;

    this.diffService.setOptions({
      mode: this.comparisonMode,
      computationAccuracy: accuracy,
      displayAccuracy: this.displayAccuracy,
      comparisonType: this.comparisonType,
      viewMode: this.viewMode,
      useLocalAxis: this.useLocalAxis,
      colors: this.colors,
      showEdges: this.showEdges,
      edgeColor: this.edgeColor,
      useHeatmapGradient: this.useHeatmapGradient,
      refineBoundary: this.refineBoundary,
      showOnlyDifferences: this.showOnlyDifferences,
      surfaceFilterMultiplier: this.surfaceFilterMultiplier,
      subdivisionDepth: this.subdivisionDepth,
      ghostOpacity: this.ghostOpacity
    });

    try {
      this.sonuc = await this.diffService.karsilastir(
        this.modelA!.nesne,
        this.modelB!.nesne,
        (progress, message) => {
          this.progressYuzde = progress;
          this.progressMesaj = message;
          this.CD.detectChanges();
        }
      );

      this.gorselOlustur();

    } catch (err) {
      console.error('CAD Diff error:', err);
    } finally {
      this.islemYapiliyor = false;
      this.CD.detectChanges();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // GÖRSELLEŞTİRME
  // ═══════════════════════════════════════════════════════════════════════════

  private gorselOlustur(): void {
    if (!this.sonuc || !this._sahne) return;

    this.gorselTemizle();

    this.vizGrup = new THREE.Group();
    this.vizGrup.name = 'CAD_DIFF_RESULT';

    // ─── Professional PBR Material ───
    const material = new THREE.MeshStandardMaterial({
      vertexColors: true,
      side: THREE.DoubleSide,
      roughness: 0.55,
      metalness: 0.15,
      polygonOffset: true,
      polygonOffsetFactor: 4,
      polygonOffsetUnits: 4,
      envMapIntensity: 0.6
    });

    // ─── Overlay mode: Calculate offset to superimpose models ───
    // Model kök pozisyonlarını kullan (bounding box değil!) - geometri farklılıkları offset'i bozmasın
    let overlayOffset: THREE.Vector3 | null = null;
    if (this.overlayMode && this.modelA?.nesne && this.modelB?.nesne) {
      const posA = new THREE.Vector3();
      const posB = new THREE.Vector3();
      this.modelA.nesne.getWorldPosition(posA);
      this.modelB.nesne.getWorldPosition(posB);
      const diff = posA.clone().sub(posB);
      // Sadece gerçekten farklı pozisyondalarsa offset uygula
      if (diff.lengthSq() > 0.001) {
        overlayOffset = diff;
      }
    }

    if (this.sonuc.meshA) {
      this.heatmapMeshA = new THREE.Mesh(this.sonuc.meshA, material.clone());
      this.heatmapMeshA.name = 'DIFF_A';
      this.heatmapMeshA.renderOrder = 0;
      this.vizGrup.add(this.heatmapMeshA);
    }

    if (this.sonuc.meshB) {
      const matB = material.clone();
      // Overlay mode: B slightly transparent so A shows through
      if (this.overlayMode) {
        matB.transparent = true;
        matB.opacity = 0.7;
        matB.depthWrite = false;
      }
      this.heatmapMeshB = new THREE.Mesh(this.sonuc.meshB, matB);
      this.heatmapMeshB.name = 'DIFF_B';
      this.heatmapMeshB.renderOrder = this.overlayMode ? 2 : 0;
      // Overlay: shift B to align with A
      if (overlayOffset) {
        this.heatmapMeshB.position.copy(overlayOffset);
      }
      this.vizGrup.add(this.heatmapMeshB);
    }

    // ─── Professional Edge Rendering ───
    if (this.showEdges) {
      const edgeColor = new THREE.Color(this.edgeColor.r, this.edgeColor.g, this.edgeColor.b);
      // "Sadece farklar" modunda daha yumuşak kenar açısı (daha az kenar çizgisi)
      const edgeAngle = this.showOnlyDifferences ? 20 : 30;

      if (this.heatmapMeshA && this.sonuc?.meshA) {
        const edgeGeoA = new THREE.EdgesGeometry(this.sonuc.meshA, edgeAngle);
        const edgeMatA = new THREE.LineBasicMaterial({
          color: edgeColor,
          depthTest: true,
          depthWrite: false,
          transparent: this.showOnlyDifferences,
          opacity: this.showOnlyDifferences ? 0.6 : 1.0
        });
        const edgeLinesA = new THREE.LineSegments(edgeGeoA, edgeMatA);
        edgeLinesA.name = 'DIFF_EDGES_A';
        edgeLinesA.frustumCulled = false;
        edgeLinesA.renderOrder = 1;
        this.vizGrup.add(edgeLinesA);
        this.edgeLinesA = edgeLinesA;
      }

      if (this.heatmapMeshB && this.sonuc?.meshB) {
        const edgeGeoB = new THREE.EdgesGeometry(this.sonuc.meshB, edgeAngle);
        const edgeMatB = new THREE.LineBasicMaterial({
          color: edgeColor,
          depthTest: true,
          depthWrite: false,
          transparent: this.showOnlyDifferences,
          opacity: this.showOnlyDifferences ? 0.6 : 1.0
        });
        const edgeLinesB = new THREE.LineSegments(edgeGeoB, edgeMatB);
        edgeLinesB.name = 'DIFF_EDGES_B';
        edgeLinesB.frustumCulled = false;
        edgeLinesB.renderOrder = this.overlayMode ? 3 : 1;
        // Overlay: shift B edges too
        if (overlayOffset) {
          edgeLinesB.position.copy(overlayOffset);
        }
        this.vizGrup.add(edgeLinesB);
        this.edgeLinesB = edgeLinesB;
      }
    }

    // ─── Ghost Mode vs Hide Originals ───
    if (this.showOnlyDifferences) {
      // Ghost mode: show originals as semi-transparent context
      this.applyGhostMode(this.modelA?.nesne, this.savedMaterialsA);
      this.applyGhostMode(this.modelB?.nesne, this.savedMaterialsB, overlayOffset);
    } else {
      // Normal mode: hide originals
      if (this.modelA?.nesne) this.modelA.nesne.visible = false;
      if (this.modelB?.nesne) this.modelB.nesne.visible = false;
    }
    this.orijinallerGizli = !this.showOnlyDifferences;

    const hedef = this._sahne.objeAna || this._sahne;
    hedef.add(this.vizGrup);

    this._sahne.anaGosterim?.ciz?.();
  }

  /**
   * Ghost Mode: Orijinal modelin materyallerini yarı-saydam gri ile değiştir.
   * Bu sayede "Sadece Farkları Göster" modunda geometrik bağlam korunur.
   */
  private applyGhostMode(
    model: THREE.Object3D | undefined | null,
    savedMaterials: Map<THREE.Mesh, THREE.Material | THREE.Material[]>,
    positionOffset?: THREE.Vector3 | null
  ): void {
    if (!model) return;

    // Create ghost material (opacity ayarlanabilir, her çağrıda yeniden oluştur)
    if (this.ghostMaterial) { this.ghostMaterial.dispose(); }
    this.ghostMaterial = new THREE.MeshStandardMaterial({
      color: 0x9aacbe,
      transparent: true,
      opacity: this.ghostOpacity,
      side: THREE.DoubleSide,
      depthWrite: false,
      roughness: 0.8,
      metalness: 0.05,
      polygonOffset: true,
      polygonOffsetFactor: 2,
      polygonOffsetUnits: 2,
    });

    model.visible = true;

    // Save originals and apply ghost
    model.traverse((child: THREE.Object3D) => {
      if (child instanceof THREE.Mesh && child.material) {
        savedMaterials.set(child, child.material);
        child.material = this.ghostMaterial!;
      }
    });

    // Hide any LineSegments in the original (edges would be distracting)
    model.traverse((child: THREE.Object3D) => {
      if (child instanceof THREE.LineSegments) {
        child.visible = false;
      }
    });
  }

  /**
   * Ghost mode'dan çık: orijinal materyalleri geri yükle
   */
  private restoreOriginalMaterials(): void {
    for (const [mesh, material] of this.savedMaterialsA) {
      mesh.material = material as any;
    }
    for (const [mesh, material] of this.savedMaterialsB) {
      mesh.material = material as any;
    }
    this.savedMaterialsA.clear();
    this.savedMaterialsB.clear();

    // Restore LineSegments visibility
    if (this.modelA?.nesne) {
      this.modelA.nesne.traverse((child: THREE.Object3D) => {
        if (child instanceof THREE.LineSegments) child.visible = true;
      });
    }
    if (this.modelB?.nesne) {
      this.modelB.nesne.traverse((child: THREE.Object3D) => {
        if (child instanceof THREE.LineSegments) child.visible = true;
      });
    }

    if (this.ghostMaterial) {
      this.ghostMaterial.dispose();
      this.ghostMaterial = null;
    }
  }

  // Hariç tutulacak isimler (stok, blank, vb.)
  private readonly EXCLUDED_NAMES = [
    'stok', 'stock', 'blank', 'raw', 'kutuk', 'kütük',
    'billet', 'workpiece', 'material', 'block', 'grid', 'helper'
  ];

  /**
   * Orijinal modelden edge (LineSegments) objelerini çıkar ve kopyala
   * Stok ve benzeri nesnelerin edge'lerini hariç tutar
   * Eğer LineSegments bulunamazsa, mesh'lerden EdgesGeometry üret
   */
  private extractOriginalEdges(source: THREE.Object3D, color: THREE.Color): THREE.LineSegments[] {
    const edges: THREE.LineSegments[] = [];

    // Önce source'un world matrix'ini güncelle
    source.updateWorldMatrix(true, true);

    // Mevcut LineSegments'leri bul
    source.traverse((child) => {
      if (child instanceof THREE.LineSegments && child.geometry) {
        // Stok kontrolü - bu edge stok'a ait mi?
        if (this.isEdgeExcluded(child)) {
          return; // Stok edge'lerini atla
        }

        // Geometry'yi klonla
        const clonedGeo = child.geometry.clone();

        // World transform'u geometry'ye direkt uygula
        clonedGeo.applyMatrix4(child.matrixWorld);

        // Yeni materyal oluştur
        const mat = new THREE.LineBasicMaterial({
          color: color,
          depthTest: false,
          depthWrite: false
        });

        const clonedLine = new THREE.LineSegments(clonedGeo, mat);
        clonedLine.frustumCulled = false;
        clonedLine.renderOrder = 999;

        edges.push(clonedLine);
      }
    });

    // Eğer hiç edge bulunamadıysa, mesh'lerden üret
    if (edges.length === 0) {
      source.traverse((child) => {
        if (child instanceof THREE.Mesh && child.geometry) {
          // Stok kontrolü
          if (this.isEdgeExcluded(child)) {
            return;
          }

          // EdgesGeometry oluştur (30 derece threshold)
          const edgeGeo = new THREE.EdgesGeometry(child.geometry, 30);

          // World transform'u uygula
          edgeGeo.applyMatrix4(child.matrixWorld);

          const mat = new THREE.LineBasicMaterial({
            color: color,
            depthTest: false,
            depthWrite: false
          });

          const edgeLine = new THREE.LineSegments(edgeGeo, mat);
          edgeLine.frustumCulled = false;
          edgeLine.renderOrder = 999;

          edges.push(edgeLine);
        }
      });
    }

    return edges;
  }

  /**
   * Edge'in stok veya hariç tutulan bir nesneye ait olup olmadığını kontrol et
   */
  private isEdgeExcluded(obj: THREE.Object3D): boolean {
    // Kendi ismini kontrol et
    const name = (obj.name || '').toLowerCase();
    for (const ex of this.EXCLUDED_NAMES) {
      if (name.includes(ex)) return true;
    }

    // Parent zincirini kontrol et
    let parent = obj.parent;
    while (parent) {
      const pName = (parent.name || '').toLowerCase();
      for (const ex of this.EXCLUDED_NAMES) {
        if (pName.includes(ex)) return true;
      }
      parent = parent.parent;
    }

    return false;
  }

  private gorselTemizle(): void {
    // Restore ghost mode materials first
    this.restoreOriginalMaterials();

    if (this.vizGrup) {
      this.vizGrup.removeFromParent();

      this.vizGrup.traverse((child) => {
        if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) {
          child.geometry?.dispose();
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else {
            (child.material as THREE.Material)?.dispose();
          }
        }
      });

      this.vizGrup = null;
      this.heatmapMeshA = null;
      this.heatmapMeshB = null;
      this.edgeLinesA = null;
      this.edgeLinesB = null;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // TOGGLE'LAR
  // ═══════════════════════════════════════════════════════════════════════════

  orijinalToggle(): void {
    this.orijinallerGizli = !this.orijinallerGizli;

    if (this.modelA?.nesne) this.modelA.nesne.visible = !this.orijinallerGizli;
    if (this.modelB?.nesne) this.modelB.nesne.visible = !this.orijinallerGizli;

    if (this.heatmapMeshA) this.heatmapMeshA.visible = this.orijinallerGizli;
    if (this.heatmapMeshB) this.heatmapMeshB.visible = this.orijinallerGizli;

    this._sahne?.anaGosterim?.ciz?.();
    this.CD.detectChanges();
  }

  farkaOdaklan(): void {
    if (!this.vizGrup || !this._sahne) return;

    const box = new THREE.Box3().setFromObject(this.vizGrup);
    if (box.isEmpty()) return;

    const center = new THREE.Vector3();
    box.getCenter(center);

    this._sahne.anaGosterim?.noktayaOdaklan?.(center);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // TEMİZLİK & EXPORT
  // ═══════════════════════════════════════════════════════════════════════════

  temizle(): void {
    if (this.modelA?.nesne) this.modelA.nesne.visible = true;
    if (this.modelB?.nesne) this.modelB.nesne.visible = true;

    this.gorselTemizle();
    this.sonuc = null;
    this.modelA = null;
    this.modelB = null;
    this.orijinallerGizli = true;

    this._sahne?.anaGosterim?.ciz?.();
    this.CD.detectChanges();
  }

  sonuclariAktar(): void {
    if (!this.sonuc) return;

    const exportData = {
      tarih: new Date().toISOString(),
      modelA: this.modelA?.isim,
      modelB: this.modelB?.isim,
      mode: this.comparisonMode,
      computationAccuracy: this.comparisonMode === 'geometric' ? this.computationAccuracy : this.visualAccuracy,
      comparisonType: this.comparisonType,
      viewMode: this.viewMode,
      maxSapma: this.sonuc.maxSapma,
      ortalamaSapma: this.sonuc.ortalamaSapma,
      toleransIcindeYuzde: this.sonuc.toleransIcindeYuzde,
      vertexSayisiA: this.sonuc.vertexSayisiA,
      vertexSayisiB: this.sonuc.vertexSayisiB,
      farkliVertexA: this.sonuc.farkliVertexA,
      farkliVertexB: this.sonuc.farkliVertexB,
      islemSuresi: this.sonuc.sure,
      tier: this.sonuc.tier
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `cad-diff-${Date.now()}.json`;
    a.click();

    URL.revokeObjectURL(url);
  }

  /**
   * İnsan-okur QC sapma raporu (CSV). Özet istatistikler + sapma histogramı.
   * Excel/LibreOffice'te doğrudan açılır (UTF-8 BOM + ; ayraç, TR yerel uyumlu).
   */
  raporAktar(): void {
    if (!this.sonuc) return;

    const tolerans = this.sonuc.renkSkalasi?.tolerans ?? 0;
    const sb: string[] = [];
    sb.push('FikoYT CAD Karşılaştırma — Sapma Raporu');
    sb.push(`Tarih;${new Date().toLocaleString('tr-TR')}`);
    sb.push(`Model A;${this.modelA?.isim ?? '-'}`);
    sb.push(`Model B;${this.modelB?.isim ?? '-'}`);
    sb.push(`Mod;${this.comparisonMode}`);
    sb.push(`Tolerans (mm);${tolerans}`);
    sb.push('');
    sb.push('Özet;Değer');
    sb.push(`Maksimum sapma (mm);${this.sonuc.maxSapma?.toFixed(4) ?? '-'}`);
    sb.push(`Ortalama sapma (mm);${this.sonuc.ortalamaSapma?.toFixed(4) ?? '-'}`);
    sb.push(`Std. sapma (mm);${this.sonuc.stdSapma?.toFixed(4) ?? '-'}`);
    const yuzdelikler = this.sapmaYuzdelikleri();
    if (yuzdelikler) {
      sb.push(`Medyan sapma (mm);${yuzdelikler.p50.toFixed(4)}`);
      sb.push(`P95 sapma (mm);${yuzdelikler.p95.toFixed(4)}`);
      sb.push(`P99 sapma (mm);${yuzdelikler.p99.toFixed(4)}`);
    }
    sb.push(`Tolerans içi (%);${this.sonuc.toleransIcindeYuzde?.toFixed(2) ?? '-'}`);
    sb.push(`Tolerans dışı vertex;${yuzdelikler ? yuzdelikler.toleransDisiAdet : '-'}`);
    sb.push(`Vertex A;${this.sonuc.vertexSayisiA ?? '-'}`);
    sb.push(`Vertex B;${this.sonuc.vertexSayisiB ?? '-'}`);
    sb.push('');

    // Sapma histogramı (her iki modelin birleşik mesafeleri)
    const histo = this.sapmaHistogrami(20);
    if (histo) {
      sb.push('Sapma Aralığı (mm);Vertex Sayısı;Yüzde (%)');
      for (const b of histo.kovalar) {
        sb.push(`${b.alt.toFixed(3)} - ${b.ust.toFixed(3)};${b.adet};${(b.adet / histo.toplam * 100).toFixed(2)}`);
      }
    }

    const csv = '﻿' + sb.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cad-diff-rapor-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /** Per-vertex mesafelerden p50/p95/p99 + tolerans dışı sayısı hesaplar */
  private sapmaYuzdelikleri(): { p50: number; p95: number; p99: number; toleransDisiAdet: number } | null {
    const diziler = [this.sonuc?.distancesA, this.sonuc?.distancesB].filter(Boolean) as Float32Array[];
    if (!diziler.length) return null;

    let toplamAdet = 0;
    for (const d of diziler) toplamAdet += d.length;
    if (!toplamAdet) return null;

    const tolerans = this.sonuc!.renkSkalasi?.tolerans ?? 0;
    const birlesik = new Float32Array(toplamAdet);
    let o = 0, toleransDisiAdet = 0;
    for (const d of diziler) {
      for (let i = 0; i < d.length; i++) {
        const v = d[i];
        if (v < 0 || isNaN(v)) continue;
        birlesik[o++] = v;
        if (tolerans > 0 && v > tolerans) toleransDisiAdet++;
      }
    }
    if (!o) return null;
    const gecerli = birlesik.subarray(0, o);
    gecerli.sort();
    const yuzde = (p: number) => gecerli[Math.min(o - 1, Math.floor(p * o))];
    return { p50: yuzde(0.5), p95: yuzde(0.95), p99: yuzde(0.99), toleransDisiAdet };
  }

  /** Per-vertex mesafelerden eşit genişlikli histogram üretir */
  private sapmaHistogrami(kovaSayisi: number): { kovalar: { alt: number; ust: number; adet: number }[]; toplam: number } | null {
    const dizilerKaynak = [this.sonuc?.distancesA, this.sonuc?.distancesB].filter(Boolean) as Float32Array[];
    if (!dizilerKaynak.length) return null;

    const max = this.sonuc!.maxSapma || 0;
    if (max <= 0) return null;

    const adim = max / kovaSayisi;
    const kovalar = Array.from({ length: kovaSayisi }, (_, i) => ({ alt: i * adim, ust: (i + 1) * adim, adet: 0 }));
    let toplam = 0;
    for (const dizi of dizilerKaynak) {
      for (let i = 0; i < dizi.length; i++) {
        const d = dizi[i];
        if (d < 0 || isNaN(d)) continue;
        let k = Math.floor(d / adim);
        if (k >= kovaSayisi) k = kovaSayisi - 1;
        kovalar[k].adet++;
        toplam++;
      }
    }
    return toplam > 0 ? { kovalar, toplam } : null;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  formatSayi(sayi: number, decimals: number = 2): string {
    if (sayi >= 1000000) return (sayi / 1000000).toFixed(1) + 'M';
    if (sayi >= 1000) return (sayi / 1000).toFixed(1) + 'K';
    return sayi.toFixed(decimals);
  }

  /**
   * Mesafe değerini otomatik birim ile formatla
   * 0.001mm → "0.001 mm", 12.5mm → "12.50 mm", 1500mm → "1.50 m"
   */
  formatMesafe(mm: number): string {
    if (mm <= 0) return '0 mm';
    if (mm >= 1000) return (mm / 1000).toFixed(2) + ' m';
    if (mm >= 10) return mm.toFixed(1) + ' mm';
    if (mm >= 1) return mm.toFixed(2) + ' mm';
    if (mm >= 0.01) return mm.toFixed(3) + ' mm';
    return mm.toExponential(1) + ' mm';
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENK YARDIMCILARI
  // ═══════════════════════════════════════════════════════════════════════════

  rgbToHex(rgb: RGB): string {
    const toHex = (c: number) => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return '#' + toHex(rgb.r) + toHex(rgb.g) + toHex(rgb.b);
  }

  hexToRgb(hex: string): RGB {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      return {
        r: parseInt(result[1], 16) / 255,
        g: parseInt(result[2], 16) / 255,
        b: parseInt(result[3], 16) / 255
      };
    }
    return { r: 0.5, g: 0.5, b: 0.5 };
  }

  onColorChange(type: 'same' | 'removed' | 'added' | 'edge', hex: string): void {
    const rgb = this.hexToRgb(hex);
    if (type === 'edge') {
      this.edgeColor = rgb;
    } else {
      this.colors[type] = rgb;
    }
    this.CD.detectChanges();
  }

  // Edge toggle
  toggleEdges(): void {
    this.showEdges = !this.showEdges;

    if (this.edgeLinesA) this.edgeLinesA.visible = this.showEdges;
    if (this.edgeLinesB) this.edgeLinesB.visible = this.showEdges;

    this._sahne?.anaGosterim?.ciz?.();
    this.CD.detectChanges();
  }

  // Preset uygula (Hassas/Standart/Hızlı)
  applyPreset(preset: 'hassas' | 'standart' | 'hizli'): void {
    this.activePreset = preset;
    switch (preset) {
      case 'hassas':
        this.surfaceFilterMultiplier = 5;
        this.subdivisionDepth = 4;
        this.ghostOpacity = 0.20;
        break;
      case 'standart':
        this.surfaceFilterMultiplier = 10;
        this.subdivisionDepth = 3;
        this.ghostOpacity = 0.20;
        break;
      case 'hizli':
        this.surfaceFilterMultiplier = 20;
        this.subdivisionDepth = 1;
        this.ghostOpacity = 0.10;
        break;
    }
    this.CD.detectChanges();
  }

  // Reset colors to default
  resetColors(): void {
    this.colors = {
      same: { r: 0.78, g: 0.72, b: 0.32 },    // Warm gold
      removed: { r: 0.82, g: 0.18, b: 0.32 },  // Coral-magenta
      added: { r: 0.18, g: 0.52, b: 0.82 }     // Steel blue
    };
    this.edgeColor = { r: 0.15, g: 0.15, b: 0.15 };
    this.CD.detectChanges();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // KARŞILAŞTIRMA PENCERESİ
  // ═══════════════════════════════════════════════════════════════════════════

  async karsilastirmaPenceresiAc(): Promise<void> {
    if (!this.sonuc || !this.modelA || !this.modelB) {
      console.warn('[FikoCadDiff] Karşılaştırma sonucu yok');
      return;
    }

    // FikoDialogCore'u al (similasyon v1 gibi)
    const gosterim = this._sahne?.anaGosterim;
    if (!gosterim?.DialogFiko) {
      console.warn('[FikoCadDiff] DialogFiko bulunamadı, NzModal kullanılıyor');
      await this.karsilastirmaPenceresiAcNzModal();
      return;
    }

    // Lazy load dialog component
    const { FikoCadDiffDialogComponent } = await import('./fiko-cad-diff-dialog.component');

    // FikoDialogCore ile taşınabilir pencere aç
    gosterim.DialogFiko.ac({
      bilesen: FikoCadDiffDialogComponent,
      ayarlar: {
        baslik: 'CAD Farklar - Karşılaştırma'
      },
      parametreler: {
        sonuc: this.sonuc,
        modelA: this.modelA,
        modelB: this.modelB,
        colors: this.colors,
        edgeColor: this.edgeColor,
        showEdges: this.showEdges
      }
    });
  }

  /**
   * NzModal ile pencere aç (fallback - DialogFiko bulunamazsa)
   */
  private async karsilastirmaPenceresiAcNzModal(): Promise<void> {
    const { FikoCadDiffDialogComponent } = await import('./fiko-cad-diff-dialog.component');

    this.modalService.create({
      nzTitle: 'CAD Farklar - Karşılaştırma',
      nzContent: FikoCadDiffDialogComponent,
      nzWidth: '460px',
      nzStyle: { top: '80px' },
      nzBodyStyle: { padding: '0' },
      nzFooter: null,
      nzMaskClosable: true,
      nzClosable: true,
      nzData: {
        sonuc: this.sonuc,
        modelA: this.modelA,
        modelB: this.modelB,
        colors: this.colors,
        edgeColor: this.edgeColor,
        showEdges: this.showEdges
      }
    });
  }
}
